import { Link } from 'react-router';
import { Alert } from '@/components/ui';
import { GoalBar, Kpi, MonthPicker, RefreshButton } from '@/components/widgets';
import { FAIXA_LABEL, formatBRL, formatCompetencia, formatDate, formatPct, nomeMes, tempoDesde } from '@/lib/format';
import type { TeamDashboard as Team } from '@/lib/types';
import { useApi } from '@/lib/useApi';
import { useMonth } from '@/lib/useMonth';

const FAIXA_TONE: Record<string, string> = { ABAIXO_DO_PISO: 'off', PROPORCIONAL: 'warn', ACELERADOR: 'ok' };

/** Painel do gestor: equipe inteira no mês. */
export function TeamDashboard() {
  const [mes, setMes] = useMonth();
  const { data: d, error, reload } = useApi<Team>(`/dashboard/team?competencia=${mes}`, 60_000);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Equipe · {formatCompetencia(mes)}</h1>
          {d && (
            <p className="muted">
              Meta individual {formatBRL(d.meta.metaIndividual)} · meta da equipe {formatBRL(d.meta.metaGlobal)}
              {d.meta.padrao && ' (padrão)'}
              {!d.fechado && <> · atualizado {tempoDesde(d.ultimaSync)}</>}
            </p>
          )}
        </div>
        <div className="row">
          <MonthPicker value={mes} onChange={setMes} />
          {!d?.fechado && <RefreshButton onDone={() => void reload()} />}
        </div>
      </header>

      {error && <Alert>{error}</Alert>}
      {!d ? (
        !error && <p className="muted">Carregando…</p>
      ) : (
        <>
          <div className="row">
            <Link className="btn btn--secondary" to="/metas">
              {d.meta.padrao ? `Definir meta de ${nomeMes(mes)}` : 'Metas'}
            </Link>
            {d.fechado ? (
              <span className="badge badge--ok">Mês fechado</span>
            ) : d.podeFechar ? (
              <Link className="btn btn--primary" to="/fechamentos">
                Fechar {nomeMes(mes)}
              </Link>
            ) : (
              <span className="muted small">Fechamento de {nomeMes(mes)} libera em {formatDate(d.liberaFechamentoEm)}</span>
            )}
          </div>

          {d.alertasAbertos > 0 && (
            <div className="callout">
              <div>
                <strong>{d.alertasAbertos} alerta(s) de custo para revisar</strong>
                <p className="muted small">Margem alta, custo zero, prejuízo ou custo alterado depois de lançado.</p>
              </div>
              <Link className="btn btn--secondary" to="/alertas">
                Ver alertas
              </Link>
            </div>
          )}
          {d.semVendedor > 0 && (
            <Alert kind="warning">
              {d.semVendedor} venda(s) de {nomeMes(mes)} sem vendedor ligado. Elas não contam para metas nem comissões.{' '}
              <Link to={`/vendas?mes=${mes}`}>Ver vendas</Link>
            </Alert>
          )}

          <dl className="kpis">
            <Kpi label="Vendido pela equipe" value={formatBRL(d.equipe.vendido)} hint={`${formatPct(d.equipe.atingimento)} de ${formatBRL(d.equipe.metaGlobal)}`} />
            <Kpi label={d.fechado ? 'Comissões' : 'Comissões previstas'} value={formatBRL(d.totais.comissoes)} hint="Sobre o lucro informado" />
            <Kpi
              label={d.fechado ? 'Bônus' : 'Bônus previstos'}
              value={formatBRL(d.totais.bonus)}
              hint={`${d.totais.acimaDoPiso} de ${d.vendedores.length} vendedores acima do piso`}
            />
            <Kpi
              label="Vendas sem custo"
              value={String(d.totais.vendasSemCusto)}
              hint={`Prazo dos vendedores: ${formatDate(d.prazoCusto)}`}
              tone={d.totais.vendasSemCusto > 0 ? 'warn' : undefined}
            />
          </dl>

          <section className="card">
            <div className="card__head">
              <h2>Meta da equipe</h2>
              <strong className="big-pct">{formatPct(d.equipe.atingimento)}</strong>
            </div>
            <GoalBar atingimento={d.equipe.atingimento} piso={1} label="Atingimento da meta da equipe" />
            <p>
              A equipe já vendeu <strong>{formatBRL(d.equipe.vendido)}</strong> de {formatBRL(d.equipe.metaGlobal)} neste mês.
            </p>
            <p className="muted small">
              {d.equipe.atingida
                ? `Meta batida: cada vendedor ativo ganha +${formatBRL(d.politica.valorColetivo)}.`
                : `Se a equipe chegar a ${formatBRL(d.equipe.metaGlobal)}, cada vendedor ativo ganha +${formatBRL(d.politica.valorColetivo)}.`}
            </p>
          </section>

          <section className="card flush">
            <h2 className="pad-h">Desempenho por vendedor</h2>
            {d.vendedores.length === 0 ? (
              <p className="muted pad">Nenhum vendedor ligado ao Conta Azul ainda.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Vendedor</th>
                      <th scope="col" className="num">Vendido</th>
                      <th scope="col" className="num">Meta</th>
                      <th scope="col">Faixa</th>
                      <th scope="col" className="num">Sem custo</th>
                      <th scope="col" className="num">Comissão</th>
                      <th scope="col" className="num">Bônus</th>
                      <th scope="col" className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.vendedores.map((v) => (
                      <tr key={v.userId} className={v.ativo ? undefined : 'row--off'}>
                        <td>
                          <Link to={`/vendas?mes=${mes}&vendedor=${v.userId}`}>{v.nome}</Link>
                          {!v.ativo && <span className="tag">inativo</span>}
                        </td>
                        <td className="num">{formatBRL(v.vendido)}</td>
                        <td className="num">{formatPct(v.atingimento)}</td>
                        <td>
                          <span className={`badge badge--${FAIXA_TONE[v.faixa]}`}>{FAIXA_LABEL[v.faixa]}</span>
                        </td>
                        <td className="num">{v.pendentes > 0 ? <strong className="kpi--warn">{v.pendentes}</strong> : '0'}</td>
                        <td className="num">{formatBRL(v.comissao)}</td>
                        <td className="num">{formatBRL(v.bonusTotal)}</td>
                        <td className="num">
                          <strong>{formatBRL(v.total)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">Total</th>
                      <td className="num">{formatBRL(d.equipe.vendido)}</td>
                      <td colSpan={3} />
                      <td className="num">{formatBRL(d.totais.comissoes)}</td>
                      <td className="num">{formatBRL(d.totais.bonus)}</td>
                      <td className="num">
                        <strong>{formatBRL(d.totais.total)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
